import useDebounce from '@cobliteam/hooks/src/useDebounce';
import type React from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import { removeDiacritics } from '../helpers';
import useFetchArticles from '../hooks/useFetchArticles';
import useFetchIAOrama from '../hooks/useFetchIAOrama';
import useHotKeys from '../hooks/useHotKeys';
import sidebarDef, { fleetCategories } from '../sidebarDef';
import { type Category, type DataWithCategory, EnumCategory, type TCounts } from '../types';
import type { Fuse, SidebarGroup } from '../types';

interface GlobalSearchContextType {
  isOpen: boolean;
  search: string;
  category: Category;
  fleetCategories: Category[];
  sidebar: typeof sidebarDef;
  results: DataWithCategory[];
  counts: TCounts;
  selectedItem: DataWithCategory | undefined;
  data: DataWithCategory[];
  fuse: Fuse<DataWithCategory>;
  handleSearch: (search: string) => void;
  resetResults: () => void;
  onChangeCategory: (params: { categoryId: string; keepSearch: boolean }) => void;
  setIsOpen: (isOpen: boolean) => void;
  setSidebar: (sidebar: typeof sidebarDef) => void;
  setSelectedItem: (item: DataWithCategory | undefined) => void;
}

interface GlobalSearchProviderProps {
  children: React.ReactNode;
  data: DataWithCategory[];
  fuse: Fuse<DataWithCategory>;
}

export const GlobalSearchContext = createContext<GlobalSearchContextType | undefined>(undefined);

const getCounts = (data: DataWithCategory[]) => {
  const categoriesKeys = sidebarDef.flatMap((group) => group.categories.map((category) => category.id));

  const counts = categoriesKeys.reduce((acc, key) => {
    acc[key as keyof TCounts] = data.filter((item) => item?.category?.id === key).length;
    return acc;
  }, {} as TCounts);
  counts.all = data.length;
  return counts;
};

export function GlobalSearchProvider({ children, data, fuse }: GlobalSearchProviderProps) {
  const initialData = data;
  const [sidebar, setSidebar] = useState(sidebarDef);
  const [isOpen, setIsOpen] = useState(false);
  const [search, handleSearch] = useState('');
  const [category, setCategory] = useState<Category>(sidebar[0].categories[0]);
  const [counts, setCounts] = useState<TCounts>(getCounts(initialData));
  const [results, setResults] = useState<DataWithCategory[]>([]);
  const [selectedItem, setSelectedItem] = useState<DataWithCategory | undefined>(initialData[0]);
  const debouncedSearch = useDebounce(search, 400);
  const { results: articles } = useFetchArticles({ search: debouncedSearch, limit: 15 });
  const { results: resultsOramaIA, isLoading: isLoadingOramaIA } = useFetchIAOrama({ search: debouncedSearch });
  useHotKeys(setIsOpen);

  const resetResults = () => {
    setResults([...resultsOramaIA, ...initialData, ...articles]);
    setCounts(getCounts([...resultsOramaIA, ...initialData, ...articles]));
  };

  const updateCategoryOfSidebar = useCallback((group: SidebarGroup, params: { loading: boolean }) => {
    const newSidebar = sidebar.map((groupSidebar) =>
      groupSidebar.id === group.id
        ? {
            ...groupSidebar,
            categories: groupSidebar.categories.map((c) => (c.id === group.categories[0].id ? { ...c, ...params } : c)),
          }
        : groupSidebar
    );
    setSidebar(newSidebar);
  }, []);

  const getSidebarCategoryById = useCallback((id: string) => {
    return sidebarDef.find((group) => group.categories.find((c) => c.id === id));
  }, []);

  const getResultsByCategory = useCallback(
    (categoryId: string) => {
      if (categoryId === EnumCategory.ALL) return results;
      return results.filter((r) => r.category.id === categoryId);
    },
    [results]
  );

  const onChangeCategory = ({ categoryId, keepSearch }: { categoryId: string; keepSearch: boolean }) => {
    if (!keepSearch) {
      handleSearch('');
      resetResults();
    }

    const resultsByCategory = getResultsByCategory(categoryId);
    const groupCategory = getSidebarCategoryById(categoryId);
    const category = groupCategory?.categories.find((c) => c.id === categoryId) as Category;

    setSelectedItem(resultsByCategory[0]);
    setCategory(category);
    handleSearch(search);
  };

  const onSearch = useCallback(async () => {
    if (debouncedSearch === '') {
      resetResults();
      return;
    }

    if (resultsOramaIA.length) {
      fuse.remove((item) => item.category.id === EnumCategory.IA_ARTICLES);
      const response = resultsOramaIA.map((item) => ({ ...item, query: debouncedSearch, category: { ...item.category, loading: isLoadingOramaIA } }));
      fuse.add(response[0]);
    } else {
      fuse.remove((item) => item.category.id === EnumCategory.IA_ARTICLES);
    }

    if (articles.length) {
      fuse.remove((item) => item.category.id === EnumCategory.ARTICLES);
      for (const article of articles) fuse.add(article);
    }

    const normalizedSearch = removeDiacritics(debouncedSearch);
    const results = fuse.search(normalizedSearch).map((r) => r.item);

    setCounts(getCounts(results));
    setResults(results);

    if (!selectedItem) {
      const selectedItem = category.id === EnumCategory.ALL ? results[0] : results.find((r) => r.category.id === category.id);
      setSelectedItem(selectedItem);
    }
  }, [debouncedSearch, resultsOramaIA, articles]);

  // Search for results
  useEffect(() => {
    onSearch();
  }, [onSearch, resultsOramaIA[0]?.id, articles]);

  // Update the sidebar and selected item category loading state
  useEffect(() => {
    const groupCategory = getSidebarCategoryById(EnumCategory.IA_ARTICLES);
    if (groupCategory) updateCategoryOfSidebar(groupCategory, { loading: isLoadingOramaIA });
    if (resultsOramaIA[0]?.id && (selectedItem?.category?.id === EnumCategory.IA_ARTICLES || category.id === EnumCategory.IA_ARTICLES)) {
      const item = resultsOramaIA[0];
      setSelectedItem({ ...item, category: { ...item.category, loading: isLoadingOramaIA } });
    }
  }, [isLoadingOramaIA, getSidebarCategoryById, updateCategoryOfSidebar]);

  useEffect(() => {
    if (!search) {
      resetResults();
    }
  }, [isOpen]);

  const value: GlobalSearchContextType = {
    data,
    fuse,
    isOpen,
    search,
    category,
    sidebar,
    fleetCategories,
    results,
    counts,
    selectedItem,
    handleSearch,
    resetResults,
    onChangeCategory,
    setIsOpen,
    setSidebar,
    setSelectedItem,
  };

  return <GlobalSearchContext.Provider value={value}>{children}</GlobalSearchContext.Provider>;
}

export default GlobalSearchProvider;
